// constants/tiProviders.ts

export interface TIProviderMeta {
  name: string;
  logo: string;
  scoreRange: [number, number];
  getSeverity: (score: number) => 'critical' | 'high' | 'medium' | 'low';
  /** Backend integration class name (e.g., 'recorded-future') */
  integrationClass: string;
  /**
   * Risk name prefixes emitted by this provider, mapped to human labels.
   * Used to query provider-specific risks via /my endpoint.
   */
  riskPrefixes: Record<string, string>;
}

/**
 * Registry of known TI providers for display purposes.
 * Add an entry here when integrating a new TI platform.
 */
export const TI_PROVIDER_REGISTRY: Record<string, TIProviderMeta> = {
  recorded_future: {
    name: 'Recorded Future',
    logo: 'recordedfuture.svg',
    scoreRange: [0, 100],
    getSeverity: (s: number) =>
      s >= 75 ? 'critical' : s >= 50 ? 'high' : s >= 25 ? 'medium' : 'low',
    integrationClass: 'recorded-future',
    riskPrefixes: {
      'rf-ioc': 'IOC Indicators',
      'rf-alert': 'Alerts',
      'rf-playbook-alert': 'Playbook Alerts',
      'rf-threat-actor': 'Threat Actors',
      'rf-threat-malware': 'Malware',
      'rf-detection-rule': 'Detection Rules',
      'rf-identity': 'Identity Exposure',
      'rf-analyst-note': 'Analyst Notes',
    },
  },
  socradar: {
    name: 'SOCRadar',
    logo: 'socradar.svg',
    scoreRange: [0, 100],
    getSeverity: (s: number) =>
      s >= 80 ? 'critical' : s >= 60 ? 'high' : s >= 30 ? 'medium' : 'low',
    integrationClass: 'socradar',
    riskPrefixes: {},
  },
};

/**
 * Resolve display metadata for a provider. Falls back to a generic entry
 * so the UI can render unknown providers without code changes.
 */
export function getTIProviderMeta(provider: string): TIProviderMeta {
  return (
    TI_PROVIDER_REGISTRY[provider] ?? {
      name: provider.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
      logo: '',
      scoreRange: [0, 100] as [number, number],
      getSeverity: (s: number) =>
        s >= 75 ? 'critical' : s >= 50 ? 'high' : s >= 25 ? 'medium' : 'low',
      integrationClass: provider,
      riskPrefixes: {},
    }
  );
}
